import React, { useCallback, useEffect, useRef, useState } from 'react';
import useGlobalShortcut from '../utils/useGlobalShortcut';

const SHORTCUTS = [
  { keys: ['/'], description: 'Focus the global search bar' },
  { keys: ['↑', '↓', 'Home', 'End'], description: 'Move through search results' },
  { keys: ['Enter'], description: 'Open the highlighted search result' },
  { keys: ['Esc'], description: 'Close search results or this panel' },
  { keys: ['←', '→', 'Home', 'End'], description: 'Move between tabs (within a tab list)' },
  { keys: ['Tab'], description: 'Move to the next focusable control' },
  { keys: ['?'], description: 'Show this panel' },
];

function Key({ children }) {
  return (
    <kbd
      style={{
        display: 'inline-block',
        padding: '2px 7px',
        borderRadius: 4,
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface-alt)',
        fontFamily: 'var(--font-data)',
        fontSize: 'var(--font-size-xs)',
        marginRight: 4,
      }}
    >
      {children}
    </kbd>
  );
}

/** A discoverable overlay of keyboard shortcuts, opened with "?" — a
 * shortcut nobody can find isn't much of a feature (Slack, Gmail, and most
 * keyboard-first apps use exactly this pattern). Escape closes it, and
 * focus returns to whatever triggered it, consistent with the "Esc closes
 * modals/dropdowns" standard from docs/UI_UX_STANDARDS.md. */
export default function KeyboardShortcutsHelp() {
  const [open, setOpen] = useState(false);
  const closeButtonRef = useRef(null);
  const previouslyFocused = useRef(null);

  const handleOpen = useCallback(() => {
    previouslyFocused.current = document.activeElement;
    setOpen(true);
  }, []);

  useGlobalShortcut('?', handleOpen);

  useEffect(() => {
    if (open) closeButtonRef.current?.focus();
  }, [open]);

  const handleClose = useCallback(() => {
    setOpen(false);
    previouslyFocused.current?.focus?.();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(28, 35, 51, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-card)',
          padding: 'var(--space-5)',
          width: 360,
          maxWidth: '90vw',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', margin: 0 }}>Keyboard Shortcuts</h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{ border: 'none', background: 'transparent', fontSize: 'var(--font-size-lg)', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
          >
            ×
          </button>
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {SHORTCUTS.map((s) => (
            <li key={s.description} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{s.description}</span>
              <span>
                {s.keys.map((k) => <Key key={k}>{k}</Key>)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
